import { useNode } from "@craftjs/core";
import type { Property } from "csstype";
import { rem } from "polished";
import * as React from "react";

import PrimitivesFloatingOptions from "@/components/editor/block-outline/floating-options/primitives-floating-component";
import type { ImagePosition } from "@/components/editor/image-positioner/image-positioner-component";
import ImageSettings from "@/components/editor/primitives/legacy/image/image-settings-component";
import useViewport from "@/hooks/use-viewport";
import getViewportValue from "@/utils/get-viewport-value";

export interface ImageStyleProps extends Partial<React.CSSProperties> {
  borderRadius?: string;
  objectFit?: Property.ObjectFit;
  scale?: string;
}

export type ImageComponentProps = {
  blockType: string;
  imageUrl: string;
  style?: ImageStyleProps;
  shape?: "circle" | "rectangle";
  // `objectPosition` is not part of `ImageStyleProps` here because
  // `<ImagePositioner />` needs the direct prop name, in our case,
  // style.objectPosition. Since it can't access deeply nested objects,
  // we need to pass `objectPosition` as a prop, and map it to the style.
  objectPosition?: ImagePosition;
  imageWidth?: [string | number, string | number, string | number];
  imageHeight?: [string | number, string | number, string | number];
};

/**
 * @deprecated Import the latest image block from /primitives
 */
export default function Image(props: ImageComponentProps) {
  const {
    connectors: { connect },
  } = useNode();

  const viewport = useViewport();

  const imageStyle = React.useMemo(() => {
    let borderRadius = props.style?.borderRadius || "0";
    if (props.shape === "circle") {
      borderRadius = "50%";
    }
    const width = getViewportValue(
      props.imageWidth?.length
        ? props.imageWidth
        : [rem(256), rem(300), rem(400)],
      viewport
    ).toString();
    const height = getViewportValue(
      props.imageHeight?.length
        ? props.imageHeight
        : [rem(256), rem(300), rem(400)],
      viewport
    ).toString();
    return {
      ...props.style,
      display: "block",
      objectFit: "cover" as const,
      margin: "auto",
      objectPosition: props.objectPosition,
      borderRadius,
      width,
      height,
    };
  }, [
    props.style,
    props.imageWidth,
    props.imageHeight,
    props.shape,
    props.objectPosition,
    viewport,
  ]);

  return (
    <div ref={connect}>
      <img
        draggable="false"
        src={props.imageUrl}
        alt=""
        style={imageStyle}
        data-testid="image block"
      />
    </div>
  );
}

Image.craft = {
  custom: {
    displayName: "Image",
  },
  related: {
    leftOutlineOptions: PrimitivesFloatingOptions,
    settings: ImageSettings,
  },
};
